import { apiClient } from './apiClient';
import { camelize } from './_caseTransform';
import type { Project, ProjectCreate, ProjectUpdate, ProjectWithStats } from '../types/project';

// Mock data for development/demo mode
const createMockProject = (data: ProjectCreate): Project => ({
  id: crypto.randomUUID(),
  name: data.name,
  label: data.label,
  organizationId: data.organizationId,
  defaultCloudRegionId: data.defaultCloudRegionId,
  createdByUserId: 'current-user',
  createdAt: new Date().toISOString(),
  updatedByUserId: 'current-user',
  updatedAt: new Date().toISOString(),
});

// LocalStorage key for mock data persistence
const STORAGE_KEY = 'gizmodata-projects';

const getStoredProjects = (): Project[] => {
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored ? JSON.parse(stored) : [];
};

const storeProjects = (projects: Project[]) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(projects));
};

export const projectService = {
  /**
   * Get all projects for an organization
   */
  async getProjects(organizationId: string): Promise<Project[]> {
    const mockProjects = getStoredProjects().filter(p => p.organizationId === organizationId);

    // The API lists all RBAC-accessible projects across orgs at GET /projects/
    // (there is no /organizations/{id}/projects route); filter to the requested
    // org client-side. The old nested path 404'd, which left ProjectContext
    // empty and wrongly triggered the "create your first project" onboarding.
    const response = camelize(await apiClient.getAll<Project>(
      `/projects/`,
      mockProjects
    ));
    return (response.data || []).filter(p => p.organizationId === organizationId);
  },

  /**
   * Get a single project by ID
   */
  async getProject(projectId: string): Promise<Project | null> {
    const mockProject = getStoredProjects().find(p => p.id === projectId);

    const response = camelize(await apiClient.get<Project>(
      `/projects/${projectId}`,
      mockProject || undefined
    ));
    return response.data;
  },

  /**
   * Get project with stats (environment count, cluster count, etc.)
   */
  async getProjectWithStats(projectId: string): Promise<ProjectWithStats | null> {
    const mockProject = getStoredProjects().find(p => p.id === projectId);
    const mockWithStats: ProjectWithStats | undefined = mockProject ? {
      ...mockProject,
      projectEnvironmentCount: 0,
      instanceCount: 0,
      memberCount: 1,
    } : undefined;

    const response = camelize(await apiClient.get<ProjectWithStats>(
      `/projects/${projectId}?expand=stats`,
      mockWithStats
    ));
    return response.data;
  },

  /**
   * Create a new project
   */
  async createProject(data: ProjectCreate): Promise<Project> {
    const mockProject = createMockProject(data);

    // API creates projects at POST /projects/ with organization_id in the body
    // (apiClient serializes the camelCase body to snake_case). The old nested
    // /organizations/{id}/projects path does not exist.
    const response = camelize(await apiClient.post<Project>(
      `/projects/`,
      data,
    ));

    // Store in localStorage for mock persistence
    if (response.source === 'mock') {
      const projects = getStoredProjects();
      projects.push(response.data);
      storeProjects(projects);
    }

    return response.data;
  },

  /**
   * Update an existing project
   */
  async updateProject(projectId: string, data: ProjectUpdate): Promise<Project> {
    const existingProjects = getStoredProjects();
    const existingIndex = existingProjects.findIndex(p => p.id === projectId);
    const { defaultCloudRegionId, ...rest } = data;
    const mockUpdated: Project | undefined = existingIndex >= 0 ? {
      ...existingProjects[existingIndex],
      ...rest,
      // null = clear the override; undefined = untouched (mirrors the API's PATCH semantics).
      defaultCloudRegionId: defaultCloudRegionId === undefined
        ? existingProjects[existingIndex].defaultCloudRegionId
        : (defaultCloudRegionId ?? undefined),
      updatedAt: new Date().toISOString(),
    } : undefined;

    // The API exposes PATCH /projects/{id} (partial update, exclude_unset) — there is no
    // PUT route. `default_cloud_region_id: null` is sent verbatim (snakeKeysDeep and
    // JSON.stringify both preserve null) and clears the override.
    const response = camelize(await apiClient.patch<Project>(
      `/projects/${projectId}`,
      data,
    ));

    // Update localStorage for mock persistence
    if (response.source === 'mock' && existingIndex >= 0) {
      existingProjects[existingIndex] = response.data;
      storeProjects(existingProjects);
    }

    return response.data;
  },

  /**
   * Delete a project
   */
  async deleteProject(projectId: string): Promise<void> {
    await apiClient.delete<void>(
      `/projects/${projectId}`,
      undefined
    );

    // Remove from localStorage for mock persistence
    const projects = getStoredProjects().filter(p => p.id !== projectId);
    storeProjects(projects);
  },
};
